import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { IBaseSearchRequest, IGridResult } from 'src/app/core/models/base';
import { ICreateDeviceCategoryRequest, IDeviceCategory, IUpdateDeviceCategoryRequest } from 'src/app/devices/models/device-category';
import { DeviceService } from 'src/app/devices/services/device.service';
import { ConfirmationDialogService } from 'src/app/shared/services/confirmation-dialog.service';
import { AppNotificationService } from 'src/app/shared/services/notification.service';
import * as Actions from "../../stores/global/device-categories/actions";
import { deviceCategoriesSelector, isLoadingSelector } from 'src/app/stores/global/device-categories/selectors';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { EditEvent, RemoveEvent } from '@progress/kendo-angular-grid';
import { DeviceCategoryValidator } from 'src/app/shared/validators/device-category-validator';


@Component({
    selector: 'app-manage-device-categories',
    templateUrl: './manage-device-categories.component.html',
    styleUrl: './manage-device-categories.component.scss',
    standalone: false
})
export class ManageDeviceCategoriesComponent implements OnInit{
  isLoading$: Observable<boolean>;
  deviceCategories$: Observable<IDeviceCategory[]>;

  constructor(    
    private route : ActivatedRoute,
    private deviceService: DeviceService,
    private appNotificationService: AppNotificationService,
    private confirmationDialogService : ConfirmationDialogService,
    private store: Store<AppStateInterface>) {
      this.isLoading$ = this.store.pipe(select(isLoadingSelector)),
      this.deviceCategories$ = this.store.pipe(select(deviceCategoriesSelector))
  }
  
  deviceCategories: IGridResult<IDeviceCategory[]>;
  public formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;
  selectedCategory: IDeviceCategory = null;
  isDialogActive = false;
  public editForm: UntypedFormGroup;
  isInEditing = false;

  public searchRequest : IBaseSearchRequest = {
    pageNumber:0, 
    pageSize:999999
  }

  ngOnInit(): void {
    this.store.dispatch(Actions.getDeviceCategories());
  }

  addHandler({ sender }){
    this.closeEditor(sender);
    this.isInEditing = false;

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(null, {
        validators: Validators.required,
        asyncValidators: DeviceCategoryValidator(this.deviceService, null),
        updateOn: 'change'}),
      hasSerialNumber: new UntypedFormControl(false, {})
    });

    sender.addRow(this.formGroup);
  }

  public editHandler(args: EditEvent): void{
    this.closeEditor(args.sender);
    this.isInEditing = true;

    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(
        args.dataItem.name, {
          validators: Validators.required, 
          asyncValidators: DeviceCategoryValidator(this.deviceService, args.dataItem.id),
          updateOn: 'blur' 
        }),
        hasSerialNumber: new UntypedFormControl(args.dataItem.hasSerialNumber, {
          updateOn: 'blur' 
        })
    });

    this.editedRowIndex = args.rowIndex;

    args.sender.editRow(args.rowIndex, this.formGroup);
  }

  public saveHandler(event) {
    const createRequest: ICreateDeviceCategoryRequest = {
      name: event.formGroup.value.name,
      hasSerialNumber: event.formGroup.value.hasSerialNumber
    }

    const updateRequest: IUpdateDeviceCategoryRequest = {
      id: event.dataItem.id,
      name: event.formGroup.value.name
    }

    if (this.isInEditing) {
      this.store.dispatch(Actions.updateDeviceCategory({ deviceCategory: updateRequest }));
    } else {
      this.store.dispatch(Actions.createDeviceCategory({ deviceCategory: createRequest }));
    }
    
    event.sender.closeRow(event.rowIndex);
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
  
  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
    this.isInEditing = false;
  }

  public removeHandler(args: RemoveEvent): void{
    var subscription : Subscription = new Subscription();
    this.confirmationDialogService.open();
    subscription = this.confirmationDialogService.decision$.subscribe(val => {
      if (val) {
        // Paste Code from above
        // remove the current dataItem from the current data source,
        // `editService` in this example
        this.store.dispatch(Actions.deleteDeviceCategory({ id: args.dataItem.id }))
        this.confirmationDialogService.updateValue(false);
      } else{
        subscription.unsubscribe();
      }
    });
  }

  closeDialog(){
    this.selectedCategory = null;
    this.isDialogActive = false;
  }

  onCancel(e: PointerEvent){
    e.preventDefault();
    this.closeDialog();
  }
}
